import { z } from 'zod/v3';
import { ExceptionKindSchema, ExceptionReasonSchema } from '../generated/enums';

/**
 * RFC-182: SCOPE-kind exceptions are resource-level wildcards — they carry
 * the literal checkId sentinel `'*'` and suppress every check for that
 * resourceId. FINDING-kind rows keep the original per-(check, resource)
 * semantics.
 */
export const SCOPE_WILDCARD_CHECK_ID = '*';
export const MAX_BULK_COMPLIANCE_EXCEPTION_RESOURCES = 100;

export const ComplianceExceptionStatusSchema = z.enum([
  'ACTIVE',
  'EXPIRING_SOON',
  'EXPIRED',
]);

export const ComplianceExceptionDTOSchema = z.object({
  id: z.string(),
  resourceId: z.string(),
  resourceName: z.string().nullable(),
  resourceType: z.string().nullable(),
  checkId: z.string(),
  kind: ExceptionKindSchema,
  reason: ExceptionReasonSchema,
  justification: z.string(),
  createdById: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
  status: ComplianceExceptionStatusSchema,
}).strict();

export const CreateComplianceExceptionInputSchema = z.object({
  resourceId: z.string().min(1),
  resourceType: z.string().nullish(),
  checkId: z.string().min(1),
  /** Defaults to FINDING. For SCOPE, the server stores checkId '*' regardless of the value passed. */
  kind: ExceptionKindSchema.nullish(),
  reason: ExceptionReasonSchema,
  justification: z.string().min(1).max(2000),
  expiresAt: z.string().datetime().nullish(),
});

export const BulkCreateComplianceExceptionsInputSchema =
  CreateComplianceExceptionInputSchema.omit({
    resourceId: true,
    resourceType: true,
  }).extend({
    runId: z.string().min(1),
    resources: z
      .array(
        z.object({
          resourceId: z.string().min(1),
          resourceType: z.string().nullish(),
        }),
      )
      .min(1)
      .max(MAX_BULK_COMPLIANCE_EXCEPTION_RESOURCES),
  });

export const BulkCreateComplianceExceptionsResultSchema = z.object({
  selected: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

export const BulkDeleteComplianceExceptionsInputSchema = z.object({
  checkId: z.string().min(1),
  action: z.enum(['DELETE_FINDING', 'RETURN_TO_SCOPE']),
  resources: z
    .array(
      z.object({
        resourceId: z.string().min(1),
        exceptionId: z.string().nullish(),
      }),
    )
    .min(1)
    .max(MAX_BULK_COMPLIANCE_EXCEPTION_RESOURCES),
});

export const BulkDeleteComplianceExceptionsResultSchema = z.object({
  selected: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

export const ListComplianceExceptionsInputSchema = z.object({
  connectionId: z.string().min(1).optional(),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
  status: ComplianceExceptionStatusSchema.optional(),
  reason: ExceptionReasonSchema.optional(),
  checkId: z.string().min(1).optional(),
  kind: ExceptionKindSchema.optional(),
  includeExpired: z.boolean().optional(),
}).refine(
  (input) => !input.connectionId || input.kind === 'FINDING' || input.kind === 'SCOPE',
  { message: 'Account exception lists support Findings and Scope only', path: ['kind'] },
);

export const PaginatedComplianceExceptionsSchema = z.object({
  items: z.array(ComplianceExceptionDTOSchema),
  totalItems: z.number().int().nonnegative(),
  reasonOptions: z.array(ExceptionReasonSchema),
  checkOptions: z.array(z.string()),
}).strict();

export type ComplianceExceptionDTO = z.infer<typeof ComplianceExceptionDTOSchema>;
export type ComplianceExceptionStatus = z.infer<
  typeof ComplianceExceptionStatusSchema
>;
export type CreateComplianceExceptionInput = z.infer<
  typeof CreateComplianceExceptionInputSchema
>;
export type BulkCreateComplianceExceptionsInput = z.infer<
  typeof BulkCreateComplianceExceptionsInputSchema
>;
export type BulkDeleteComplianceExceptionsInput = z.infer<
  typeof BulkDeleteComplianceExceptionsInputSchema
>;
export type ListComplianceExceptionsInput = z.infer<
  typeof ListComplianceExceptionsInputSchema
>;
export type PaginatedComplianceExceptions = z.infer<
  typeof PaginatedComplianceExceptionsSchema
>;
